import React, { useEffect, useState } from "react";
import "./ProductItem.scss";
import {
  EditOutlined,
  EnvironmentFilled,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { Button, Card, Checkbox, Rate } from "antd";
import { useNavigate } from "react-router-dom";
import { useUser } from "contexts/UserContext";
import { useCart } from "contexts/CartContext";
import { useUnit } from "contexts/UnitContext";
import feedbackApi from "services/FeedbackApi";
import Helpers from "@utils/Helpers";

interface IProductItem {
  data: any;
  isAuction: boolean;
  type: string;
  index: number;
  setSelectedProductId?: (value: null | number) => void;
  selectedProduct?: number[];
  setSelectedProduct?: (value: number[]) => void;
}
const defaultImg =
  "https://hopeful-luck-fc0d448ef2.media.strapiapp.com/default_0a174d27c8.jpeg";
const ProductItem: React.FC<IProductItem> = ({
  data,
  isAuction,
  type,
  index,
  setSelectedProductId,
  selectedProduct,
  setSelectedProduct,
}) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { units, convertCurrency } = useUnit();
  const { isNewProductCreated, setIsNewProductState } = useUser();
  const isPromoted = type === "Promoted Products ⚡";
  const [isBlink, setIsBlink] = useState(
    isNewProductCreated === true ? true : false
  );
  const [currentImg, setCurrentImg] = useState(
    isPromoted
      ? data?.product?.images[0] || defaultImg
      : data?.images[0] || defaultImg
  );
  const [animations, setAnimations] = useState<{ x: number; y: number }[]>([]);
  const [reviewCount, setReviewCount] = useState<number | null>(null);
  const [avgRating, setAvgRating] = useState<number | null>(null);
  const [dateLeft, setDateLeft] = React.useState({
    days: "",
    hours: "",
    minutes: "",
  });
  const productUnit = isPromoted ? data?.product?.unit : data.unit;
  const productId = isPromoted ? data?.product?.product_id : data?.product_id;
  const productShape =
    data.shape == "lsquare"
      ? "Large Square Bales"
      : data.shape == "ssquare"
      ? "Small Square Bales"
      : "Round Bales";

  useEffect(() => {
    setTimeout(() => {
      setIsBlink(false);
      setIsNewProductState(false);
    }, 500);
  }, []);

  const handleAddToCard = (e: any, productId: number, quantity: number) => {
    e.stopPropagation();
    const payload = {
      product_id: productId,
      quantity: quantity,
    };
    addToCart(payload);

    const rect = e.currentTarget.getBoundingClientRect();

    setAnimations((prev) => [...prev, { x: rect.left, y: rect.top }]);
    setTimeout(() => {
      setAnimations((prev) => prev.slice(1));
    }, 300);
  };

  const handleClickProduct = (event: any) => {
    if (type === "My Products") {
      setSelectedProductId?.(data.product_id);
    } else {
      if (event.ctrlKey || event.metaKey) {
        window.open(`/product/detail?productId=${data.product_id}`, "_blank");
      } else {
        navigate(`/product/detail?productId=${data.product_id}`);
      }
    }
  };

  useEffect(() => {
    if (data?.end_time) {
      getDateLeft(data.end_time);
    }
  }, [data?.end_time]);

  useEffect(() => {
    if (!productId) {
      return;
    }
    let active = true;
    feedbackApi
      .getProductFeedbackSummary(productId)
      .then((response: any) => {
        if (active && response?.status === 200) {
          setReviewCount(response.data?.total ?? 0);
          setAvgRating(response.data?.avg_rating ?? null);
        }
      })
      .catch(() => {
        if (active) {
          setReviewCount(0);
        }
      });
    return () => {
      active = false;
    };
  }, [productId]);

  const getDateLeft = (endDate: string) => {
    const end = new Date(endDate);
    const currentDate = new Date();
    const timeDiff = end.getTime() - currentDate.getTime();
    const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const hoursLeft = Math.ceil(
      (timeDiff % (1000 * 3600 * 24)) / (1000 * 3600)
    );
    const minutesLeft = Math.ceil((timeDiff % (1000 * 3600)) / (1000 * 60));
    setDateLeft({
      days: daysLeft < 10 ? `0${daysLeft}` : daysLeft.toString(),
      hours: hoursLeft < 10 ? `0${hoursLeft}` : hoursLeft.toString(),
      minutes: minutesLeft < 10 ? `0${minutesLeft}` : minutesLeft.toString(),
    });
  };

  const calculatePrice = (): string => {
    let rawPrice = isPromoted
      ? data?.discounted_price
      : productUnit == "Ton"
      ? data.price_per_tons
      : data.price_per_bale;
    var finalPrice = rawPrice;

    return Helpers.formatNumber(
      convertCurrency(finalPrice, "USD", units.currency),
      2
    );
  };
  return (
    <div
      className={`ProductItem${index === 0 ? (isBlink ? " blink" : "") : ""}`}
    >
      <Card
        hoverable
        cover={
          <img
            src={currentImg}
            className="product-image"
            height={150}
            alt="product"
            onError={() =>
              setCurrentImg(
                "https://hopeful-luck-fc0d448ef2.media.strapiapp.com/default_0a174d27c8.jpeg"
              )
            }
          />
        }
        onClick={handleClickProduct}
      >
        <div className="product-type">
          {data.product_category_id === 1 ? "Hay" : "Straw"}
        </div>
        {type === "My Products" && (
          <div
            className={`product-sell-status ${
              data.quantity > 0 ? "selling" : "sold"
            }`}
          >
            {data.quantity > 0 ? "Posted/Selling" : "Sold/Ended"}
          </div>
        )}
        <div className="product-info">
          <div
            className={`product-name ${
              type === "My Products" && "d-flex align-items-center"
            }`}
          >
            {data.hay_type || data?.product?.product_type_name}
            {type === "My Products" && (
              <span className="ms-auto">
                <Checkbox
                  checked={selectedProduct?.includes(data.product_id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProduct?.([
                        ...selectedProduct!,
                        data.product_id,
                      ]);
                    } else {
                      setSelectedProduct?.(
                        selectedProduct!.filter(
                          (item) => item !== data.product_id
                        )
                      );
                    }
                  }}
                />
              </span>
            )}
          </div>
          <div className="product-rating">
            <Rate disabled value={avgRating ?? data.rate ?? 0} />
            <span className="ms-1">
              {reviewCount === null ? "..." : `${reviewCount} reviews`}
            </span>
          </div>
          <div className="product-price">
            <div className="price">
              ${calculatePrice()} / {productUnit == "Ton" ? "Ton" : "Bale"}
            </div>
            {isPromoted && (
              <div className="origin-price">
                $
                {Helpers.formatNumber(
                  convertCurrency(
                    productUnit == "Ton"
                      ? data.price_per_tons
                      : data.price_per_bale,
                    "USD",
                    units.currency
                  ),
                  2
                )}
                /{productUnit == "Ton" ? "Ton" : "Bale"}
              </div>
            )}
            <div className="shape">{productShape}</div>
            <div className="quantity">
              Quantity: {isPromoted ? data.discounted_quantity : data.quantity}{" "}
            </div>
            {type === "My Products" && (
              <div className="expire-date">End: 07/24/2025</div>
            )}
          </div>
          {isPromoted && (
            <div className="ad-duration">
              <div className="ad-time-group">
                <div className="time-item">
                  <div className="value">{dateLeft.days}</div>
                  <div className="unit">days</div>
                </div>
                <div className="time-item">
                  <div className="value">{dateLeft.hours}</div>
                  <div className="unit">hrs</div>
                </div>
                <div className="time-item">
                  <div className="value">{dateLeft.minutes}</div>
                  <div className="unit">mins</div>
                </div>
              </div>
            </div>
          )}
          <div className="product-action">
            <div className="location">
              <EnvironmentFilled />{" "}
              {!data?.farm_details?.farm_city
                ? data?.product?.address.city
                : data?.farm_details?.farm_city + ","}{" "}
              {data?.farm_details?.farm_province ||
                data?.product?.address?.province}
            </div>
            {type !== "My Products" ? (
              <>
                <Button
                  className="add-cart-btn"
                  onClick={(e) =>
                    handleAddToCard(e, data.product_id, data.quantity)
                  }
                  icon={<ShoppingCartOutlined />}
                />
                <div className="cart-animation-container">
                  {animations.map((animation, index) => (
                    <Button
                      icon={<ShoppingCartOutlined />}
                      key={index}
                      className="cart-animation"
                      style={{
                        left: `${animation.x}px`,
                        top: `${animation.y}px`,
                      }}
                    ></Button>
                  ))}
                </div>
              </>
            ) : (
              <Button
                className="add-cart-btn"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                icon={<EditOutlined />}
              />
            )}
          </div>
        </div>
        {isAuction && (
          <div className="aution-duration">
            <div className="title">Time left</div>
            <div className="auction-time-group">
              <div className="time-item">
                <div className="value">0</div>
                <div className="unit">Days</div>
              </div>
              <div className="time-item">
                <div className="value">0</div>
                <div className="unit">Hours</div>
              </div>
              <div className="time-item">
                <div className="value">0</div>
                <div className="unit">Mins</div>
              </div>
              <div className="time-item">
                <div className="value">0</div>
                <div className="unit">Secs</div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ProductItem;
